import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "src/store";

import {
    NewCommandDialog,
    useNewCommandDialogChunk,
} from "@dashboard/devices/components/Commands/NewCommandDialog/NewCommandDialog.lazy";
import { usePolicyContentUpdate } from "@dashboard/policies/hooks/usePolicyContentUpdate/usePolicyContentUpdate";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import PolicyCommandsTable from "@dashboard/shared/components/PolicyCommands/PolicyCommandsTable";
import { selectDevicesByGroupIds } from "@devices/store/selectors/devices";
import { MdmPolicyCommand } from "@devices/types";
import { Box } from "@mui/material";
import { SecondaryButton } from "@shared/CustomButton";

import { useStyles } from "./CommandsSection.style";

type Props = {
    policy: API.PolicyViewModel | null;
    readOnly?: boolean;
};

export const CommandsSection = ({ policy, readOnly = false }: Props) => {
    const [isAddCommandDialogOpen, setIsAddCommandDialogOpen] = React.useState(false);
    const { isLoading: isDialogLoading, load: loadDialog } = useNewCommandDialogChunk();

    const { t } = useTranslation(["pages", "common"]);
    const { classes } = useStyles();

    const { updateContent, isLoading } = usePolicyContentUpdate();

    const content = policy?.content ?? [];
    const groupIds = policy?.groupIds ?? [];

    const policyGroupDevices = useSelector((state) => selectDevicesByGroupIds(state, groupIds));

    const onUpdate = (updatedContent: MdmPolicyCommand[]) =>
        policy ? updateContent({ id: policy.id, name: policy.name, content: updatedContent }) : Promise.resolve(false);

    const openAddCommandDialog = async () => {
        if (await loadDialog()) {
            setIsAddCommandDialogOpen(true);
        }
    };

    const appendCommandAndSave = async (json: Record<string, unknown>) => {
        await onUpdate([...content, json as MdmPolicyCommand]);
    };

    const getTooltipProps = () => {
        if (isDialogLoading) {
            return { title: t("common:tooltip.featureLoading"), hide: false };
        }

        if (readOnly) {
            return { title: t("common:tooltip.cannotManagePolicies"), hide: false };
        }

        if (!policy) {
            return { title: t("pages:policiesPage.tooltip.noPolicySelected"), hide: false };
        }

        return { title: "", hide: true };
    };

    return (
        <CollapsibleProvider initialValue disabled={!policy}>
            <CollapsibleSectionSummary
                action={
                    <SecondaryButton
                        startIcon={<i className="fa-solid fa-plus" />}
                        onClick={openAddCommandDialog}
                        disabled={!policy || readOnly || isDialogLoading}
                        loading={isDialogLoading}
                        tooltipProps={getTooltipProps()}
                    >
                        {t("pages:policiesPage.button.addRule")}
                    </SecondaryButton>
                }
            >
                <CollapsibleSectionTitle>{t("pages:policiesPage.section.rules.title")}</CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails>
                <Box className={content.length ? classes.tableWrapper : classes.tableWrapperEmpty}>
                    <PolicyCommandsTable
                        content={content}
                        onUpdate={readOnly ? undefined : onUpdate}
                        loading={isLoading}
                        readOnly={readOnly}
                    />
                </Box>
            </CollapsibleSectionDetails>
            {policy && isAddCommandDialogOpen ? (
                <NewCommandDialog
                    isPolicy
                    title={t("pages:policiesPage.button.addRule")}
                    devices={policyGroupDevices}
                    toggleDialog={() => setIsAddCommandDialogOpen(false)}
                    createCommand={appendCommandAndSave}
                    loading={isLoading}
                />
            ) : null}
        </CollapsibleProvider>
    );
};
